import styled from "@emotion/styled";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../library/axios-index";
import { addCommas, numberOnly } from "../shared/common";
import dayjs from "dayjs";
import PasswordModal from "../compontents/PasswordModal";

function ChevronIcon({ up }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {up ? (
        <polyline points="18 15 12 9 6 15" />
      ) : (
        <polyline points="6 9 12 15 18 9" />
      )}
    </svg>
  );
}

function DetailRow({ label, value }) {
  return (
    <Row>
      <RowLabel>{label}</RowLabel>
      <RowValue>{value}</RowValue>
    </Row>
  );
}

export function NewPayStub() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [salaryAmountAndTex, setSalaryAmountAndTex] = useState(null);
  const [attendCountAndWorkingHour, setAttendCountAndWorkingHour] =
    useState(null);
  const [incomeTaxTxt, setIncomeTaxTxt] = useState(null);
  const [nationalPensionTaxRate, setNationalPensionTaxRate] = useState(null);
  const [healthInsuranceTaxRate, setHealthInsuranceTaxRate] = useState(null);
  const [longTermCareInsuranceTaxRate, setLongTermCareInsuranceTaxRate] =
    useState(null);
  const [employmentInsuranceTaxRate, setEmploymentInsuranceTaxRate] =
    useState(null);
  const [payStubLoad, setPayStubLoad] = useState(false);
  const [openPasswordModal, setOpenPasswordModal] = useState(true);
  const [isDeductionExpanded, setIsDeductionExpanded] = useState(false);

  const realAmount =
    (salaryAmountAndTex?.totalSalaryAmt || 0) -
    (salaryAmountAndTex?.totalTax || 0);

  useEffect(() => {
    const param = params.get("key");

    if (!param) {
      alert("데이터가 입력되지 않았습니다.");
      navigate("/");
    }
  }, []);
  useEffect(() => {
    if (payStubLoad) {
      (async () => {
        try {
          const {
            data: { result },
          } = await axiosInstance.post(`/webview/pay-stub`, {
            key: params.get("key"),
          });
          setUser(result.user);
          setAttendCountAndWorkingHour(result.attendCountAndWorkingHour);
          setSalaryAmountAndTex(result.salaryAmountAndTax);
          setIncomeTaxTxt(result.incomeTaxTxt);

          setNationalPensionTaxRate(result.nationalPensionTaxRate);
          setHealthInsuranceTaxRate(result.healthInsuranceTaxRate);
          setLongTermCareInsuranceTaxRate(result.longTermCareInsuranceTaxRate);
          setEmploymentInsuranceTaxRate(result.employmentInsuranceTaxRate);

          window.flutter_inappwebview?.callHandler("success", {
            message: "정상적으로 조회되었습니다.",
          });
        } catch (e) {
          alert(e.response?.data?.message);
          window.flutter_inappwebview?.callHandler("error", {
            message: e.response?.data?.message,
          });
          navigate("/");
        }
      })();
    }
  }, [payStubLoad]);

  const passwordCheck = useCallback(() => {
    const pwd = document.getElementById("passwordInput");
    handlePasswordCheck(pwd.value);
  }, []);

  const handlePasswordCheck = (password) => {
    if (password.length != 6) {
      alert("주민등록번호/외국인등록번호 앞자리 6자리를 입력해주세요");
      return;
    }

    (async () => {
      try {
        const {
          data: { result },
        } = await axiosInstance.post(`/webview/pay-stub/validate`, {
          key: params.get("key"),
          password: password,
        });
        setOpenPasswordModal(false);
        setPayStubLoad(true);
      } catch (e) {
        alert(e.response?.data?.message);
      }
    })();
  };

  return payStubLoad ? (
    <Content>
      <Board>
        <BoardHeader>
          {`${dayjs(user?.work_dt).format("YYYY년 M월분 급여명세서")}`}
        </BoardHeader>

        <DetailWrap>
          <HeroCard>
            <NameRow>
              <HeroName>{user?.user_nm}</HeroName>
              {user?.job_position && <HeroSub>({user.job_position})</HeroSub>}
            </NameRow>
            <HeroAmount>{addCommas(realAmount)}원</HeroAmount>
            <HeroAmountLabel>차인지급액</HeroAmountLabel>
          </HeroCard>

          <SectionCard>
            <SectionTitle>근로자 정보</SectionTitle>
            <DetailRow label="성명" value={user?.user_nm} />
            <DetailRow label="직종" value={user?.job_position} />
            <DetailRow
              label="생년월일"
              value={dayjs(user?.birth_day).format("YYYY년 M월 D일")}
            />
            <DetailRow
              label="입사일"
              value={dayjs(user?.join_dt).format("YYYY년 M월 D일")}
            />
            <DetailRow label="소속" value={user?.site_nm} />
          </SectionCard>

          <SectionCard>
            <SectionTitle>급여 산정 상세</SectionTitle>
            <DetailRow
              label="기본급"
              value={`${addCommas(salaryAmountAndTex?.totalDefaultSalaryAmt)}원`}
            />
            <DetailRow
              label="연장근로수당"
              value={`${addCommas(salaryAmountAndTex?.totalNightSalaryAmt)}원`}
            />
            <DetailRow
              label="휴일근로수당"
              value={`${addCommas(salaryAmountAndTex?.totalHolidaySalaryAmt)}원`}
            />
            <DetailRow
              label="식대"
              value={`${addCommas(salaryAmountAndTex?.taxMeal)}원`}
            />
            <DetailRow
              label="차량유지비"
              value={`${addCommas(salaryAmountAndTex?.taxTrans)}원`}
            />
            <DetailRow
              label="직책수당"
              value={`${addCommas(salaryAmountAndTex?.taxPosition)}원`}
            />
            <DetailRow
              label="기타수당"
              value={`${addCommas(salaryAmountAndTex?.taxEtc)}원`}
            />
            <DetailRow
              label="주휴수당"
              value={`${addCommas(salaryAmountAndTex?.weeklyHolidayPay)}원`}
            />
            <Separator />
            <DetailRow
              label="지급합계"
              value={`${addCommas(salaryAmountAndTex?.totalSalaryAmt)}원`}
            />

            <DeductionBlock>
              <DeductionToggle
                onClick={() => setIsDeductionExpanded(!isDeductionExpanded)}
              >
                <DeductionToggleLeft>
                  <DeductionToggleLabel>공제합계</DeductionToggleLabel>
                  <ChevronIcon up={isDeductionExpanded} />
                </DeductionToggleLeft>
                <DeductionAmount>
                  {addCommas(salaryAmountAndTex?.totalTax)}원
                </DeductionAmount>
              </DeductionToggle>

              {isDeductionExpanded && (
                <DeductionPanel>
                  <DetailRow
                    label="소득세"
                    value={`${addCommas(salaryAmountAndTex?.incomeTax)}원`}
                  />
                  <DetailRow
                    label="지방소득세"
                    value={`${addCommas(salaryAmountAndTex?.localIncomeTax)}원`}
                  />
                  <DetailRow
                    label="국민연금"
                    value={`${addCommas(salaryAmountAndTex?.nationalPension)}원`}
                  />
                  <DetailRow
                    label="건강보험"
                    value={`${addCommas(salaryAmountAndTex?.healthInsurance)}원`}
                  />
                  <DetailRow
                    label="장기요양보험"
                    value={`${addCommas(salaryAmountAndTex?.longTermCareInsurance)}원`}
                  />
                  <DetailRow
                    label="고용보험"
                    value={`${addCommas(salaryAmountAndTex?.employmentInsurance)}원`}
                  />
                </DeductionPanel>
              )}
            </DeductionBlock>
          </SectionCard>
          <SectionCard>
            <SectionTitle>근로 내역</SectionTitle>
            <DetailRow
              label="근로일"
              value={attendCountAndWorkingHour?.totalAttendCount}
            />
            <DetailRow
              label="총 근로시간"
              value={attendCountAndWorkingHour?.totalWorkingHours}
            />
            <DetailRow
              label="기본근로시간"
              value={attendCountAndWorkingHour?.totalDefaultWorkingHours}
            />
            <DetailRow
              label="연장근로시간"
              value={attendCountAndWorkingHour?.totalNightWorkingHours}
            />
            <DetailRow
              label="휴일근로시간"
              value={attendCountAndWorkingHour?.totalHolidayWorkingHours}
            />
          </SectionCard>

          <SectionCard>
            <SectionTitle>산출식</SectionTitle>
            <DetailRow label="기본급" value="기본근로시간 * 통상시급" />
            <DetailRow label="연장근로수당" value="연장근로시간 * 통상시급" />
            <DetailRow label="휴일근로수당" value="휴일근로시간 * 통상시급" />
            <DetailRow label="소득세" value={incomeTaxTxt} />
            <DetailRow label="지방소득세" value="소득세 * 10%" />
            <DetailRow
              label="국민연금"
              value={`과세급여 * ${nationalPensionTaxRate}%`}
            />
            <DetailRow
              label="건강보험"
              value={`과세급여 * ${healthInsuranceTaxRate}%`}
            />
            <DetailRow
              label="장기요양보험"
              value={`건강보험 * ${longTermCareInsuranceTaxRate}%`}
            />
            <DetailRow
              label="고용보험"
              value={`과세급여 * ${employmentInsuranceTaxRate}%`}
            />
          </SectionCard>

          <CorpName>{user?.corp_nm}</CorpName>
        </DetailWrap>
      </Board>
    </Content>
  ) : (
    <PasswordModal
      opend={openPasswordModal}
      closeModal={() => {
        setOpenPasswordModal(false);
      }}
      header={"비밀번호 입력"}
      okText={"확인"}
      handleOk={() => {}}
      widthCheck={"60%"}
    >
      <div>
        개인정보 보호를 위해 암호화된 보안 메시지 입니다. 내용을 확인하시려면
        주민등록번호/외국인등록번호 앞자리 6자리를 입력해주세요.
      </div>
      <br />
      <div style={{ display: "flex", flexDirection: "row" }}>
        <Input
          id={"passwordInput"}
          style={{ width: "85%" }}
          placeholder="주민등록번호/외국인등록번호 앞자리 6자리"
          maxLength={6}
          onKeyDown={(e) =>
            e.key === "Enter" && handlePasswordCheck(e.target.value)
          }
          onChange={(e) => {
            numberOnly(e);
          }}
        ></Input>
        <Button
          style={{ color: "#fff", background: "#3279F5", width: "15%" }}
          onClick={() => {
            passwordCheck();
          }}
        >
          {"확인"}
        </Button>
      </div>
    </PasswordModal>
  );
}

const Content = styled.div`
  display: flex;
  flex-direction: column;

  @media only screen and (min-width: 992px) {
    flex-direction: row;
  }
`;

const Board = styled.div`
  border-radius: 16px;
  background: none;
  width: 100%;
  margin-top: 10px;
  @media only screen and (min-width: 992px) {
    margin-top: 0;
  }
`;

const BoardHeader = styled.div`
  margin-bottom: 1px;
  margin-top: 1px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 32px;
  font-size: 24px;
  font-weight: 300;
  white-space: nowrap;

  h2,
  h5 {
    margin: 0;
  }

  h5 {
  }
`;

const DetailWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 24px 24px;
`;

const HeroCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 16px;
  text-align: center;
  background: rgba(239, 246, 255, 0.5);
  border: 1px solid #dbeafe;
  border-radius: 8px;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
`;

const HeroName = styled.span`
  font-weight: 700;
  color: #111827;
`;

const HeroSub = styled.span`
  font-size: 12px;
  color: #6b7280;
`;

const HeroAmount = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #1d4ed8;
  letter-spacing: -0.025em;
`;

const HeroAmountLabel = styled.div`
  margin-top: 4px;
  font-size: 12px;
  font-weight: 500;
  color: rgba(37, 99, 235, 0.8);
`;

const SectionCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  border-radius: 8px;
`;

const SectionTitle = styled.div`
  margin-bottom: 8px;
  padding-bottom: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
`;

const RowLabel = styled.span`
  color: #6b7280;
`;

const RowValue = styled.span`
  font-weight: 500;
  color: #111827;
  text-align: right;
`;

const Separator = styled.div`
  margin: 8px 0;
  height: 1px;
  background: #e5e7eb;
`;

const DeductionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DeductionToggle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 -8px;
  padding: 6px 8px;
  cursor: pointer;
  border: 1px solid transparent;
  border-radius: 6px;

  &:hover {
    background: #f3f4f6;
    border-color: #e5e7eb;
  }
`;

const DeductionToggleLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #9ca3af;
`;

const DeductionToggleLabel = styled.span`
  font-weight: 500;
  color: #4b5563;
  border-bottom: 1px dashed #9ca3af;
`;

const DeductionAmount = styled.span`
  font-weight: 500;
  color: #dc2626;
`;

const DeductionPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 4px;
  padding: 12px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const CorpName = styled.div`
  margin-top: 4px;
  text-align: right;
  font-size: 14px;
  color: #4b5563;
`;
const Input = styled.input`
  display: inline-block;
  -webkit-appearance: none;
  -moz-appearance: none;
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  appearance: none;
  box-sizing: border-box;
  width: 100%;
  //border: 1px;
  outline: none;

  padding: 12px 20px;

  color: #171923;
  leading-trim: both;
  text-edge: cap;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;

  background: #fff;
  border: 2px solid #cbd5e0;
  border-radius: 6px;
  margin-bottom: 12px;
  //margin-top: 6px;

  :focus {
  }
`;

export const Button = styled.div`
  display: flex;
  height: 24px;
  padding: 10px 20px;
  justify-content: center;
  align-items: center;
  gap: 6px;
  border-radius: 6px;
  background: #edf2f7;
  cursor: pointer;
  color: #1a202c;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  margin-left: 6px;

  img {
    width: 14px;
  }
`;
